import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { FileSignature, Check, Eraser, ShieldCheck } from "lucide-react";

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);

  useEffect(() => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1B211A";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: (p.clientX - rect.left) * (canvasRef.current.width / rect.width), y: (p.clientY - rect.top) * (canvasRef.current.height / rect.height) };
  };
  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    hasInk.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasInk.current) onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    hasInk.current = false;
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={600}
        height={180}
        className="w-full bg-[#F7F5EF] rounded-xl border-2 border-dashed border-[#546643]/50 touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <button type="button" onClick={clear} className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white mt-2" data-testid="signature-clear">
        <Eraser className="w-3.5 h-3.5" /> Clear signature
      </button>
    </div>
  );
}

export default function SignContract() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [error, setError] = useState("");
  const [typedName, setTypedName] = useState("");
  const [sig, setSig] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/contracts/${requestId}`);
        setContract(data);
        if (data.status === "signed") setDone(true);
      } catch (e) {
        setError(e?.response?.data?.detail || "Contract not found. Please log in with the account it was sent to.");
      }
    })();
  }, [requestId]);

  const submit = async () => {
    if (typedName.trim().length < 3) { alert("Type your full legal name."); return; }
    if (!sig) { alert("Draw your signature in the box."); return; }
    if (!consent) { alert("Please check the consent box to sign electronically."); return; }
    setBusy(true);
    try {
      await api.post(`/contracts/${requestId}/sign`, { typed_name: typedName.trim(), signature_data_url: sig, consent: true });
      setDone(true);
    } catch (e) {
      alert(e?.response?.data?.detail || "Signing failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (error) return (
    <section className="section dark-zone"><div className="container-if text-center py-20">
      <p className="text-sm text-red-300" data-testid="sign-error">{error}</p>
      <Link to="/login" className="btn-gold !py-2 !px-5 !text-[11px] mt-6 inline-flex">Log In</Link>
    </div></section>
  );
  if (!contract) return <section className="section dark-zone"><div className="container-if text-center py-20 text-sm text-white/50">Loading contract…</div></section>;

  return (
    <section className="section dark-zone" data-testid="sign-page">
      <div className="container-if max-w-4xl">
        {done ? (
          <div className="card-dark p-10 text-center" data-testid="sign-success">
            <div className="w-14 h-14 rounded-full bg-[#4F5D1E]/15 border border-[#4F5D1E]/40 flex items-center justify-center mx-auto mb-5">
              <Check className="w-7 h-7 text-[#4F5D1E]" />
            </div>
            <h1 className="font-display text-3xl text-white mb-3">Contract signed.</h1>
            <p className="text-sm text-white/65 max-w-md mx-auto" style={{ fontFamily: "Inter, sans-serif" }}>
              Your signed copy of <strong className="text-white">{contract.template_name}</strong> — including the signature certificate — is stored in your Secure Documents and has been emailed to you.
            </p>
            <button onClick={() => navigate("/dashboard")} className="btn-gold !py-2.5 !px-6 !text-[12px] mt-7" data-testid="sign-go-dashboard">Go to Dashboard</button>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <div className="font-mono-label text-[var(--if-gold)] mb-2 flex items-center gap-2">
                <FileSignature className="w-4 h-4" /> Signature Required
              </div>
              <h1 className="font-display text-3xl sm:text-4xl text-white mb-2">{contract.template_name}</h1>
              {contract.note && <p className="text-sm text-white/60" style={{ fontFamily: "Inter, sans-serif" }}>{contract.note}</p>}
            </div>

            <div className="card-dark p-2 mb-8">
              <iframe title="Contract" src={contract.view_url} className="w-full h-[560px] rounded-xl bg-white" data-testid="contract-viewer" />
            </div>

            <div className="card-dark p-7">
              <h2 className="font-display text-xl text-white mb-5">Sign below</h2>
              <div className="flex flex-col gap-4">
                <div>
                  <label className="font-mono-label text-white/50 block mb-1.5">Full legal name</label>
                  <input className="if-input" placeholder="e.g. John A. Martinez" value={typedName}
                    onChange={(e) => setTypedName(e.target.value)} data-testid="sign-typed-name" />
                </div>
                <div>
                  <label className="font-mono-label text-white/50 block mb-1.5">Draw your signature</label>
                  <SignaturePad onChange={setSig} />
                </div>
                <label className="flex items-start gap-2.5 text-xs text-white/70 cursor-pointer" style={{ fontFamily: "Inter, sans-serif" }}>
                  <input type="checkbox" className="mt-0.5" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="sign-consent" />
                  <span>I agree to conduct this transaction electronically and intend this electronic signature to be legally binding, the same as a handwritten signature (U.S. ESIGN Act). I have read and agree to the terms of this contract.</span>
                </label>
                <div className="flex items-center justify-between mt-2">
                  <span className="inline-flex items-center gap-1.5 text-xs text-white/45" style={{ fontFamily: "Inter, sans-serif" }}>
                    <ShieldCheck className="w-3.5 h-3.5 text-[#4F5D1E]" /> Time-stamped audit trail included
                  </span>
                  <button onClick={submit} disabled={busy} className="btn-gold !py-2.5 !px-7 !text-[12px] disabled:opacity-60" data-testid="sign-submit">
                    {busy ? "Signing…" : "Sign Contract"}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
